import React from 'react';
import styled from 'styled-components';
import Icon from 'components/icon';
import Button from 'components/button';
import PetPolicyModal from './pet-policy-modal';

const Wrapper = styled.div`
  .descriptionContainer {
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

const Row = styled.div`
  margin: 35px 0px;
`;

const FieldWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  font-size: 16px;
  .fieldLabel {
    font-size: 13px;
    color: #777;
    margin-bottom: 5px;
  }
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
`;

const Details = styled.div`
  .detailsLabel {
    font-weight: bold;
    margin-bottom: 7px;
  }
`;

const Amenities = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const hasValue = (val) => (Array.isArray(val) ? val.length > 0 : !!val);

const getPetsValue = (location) => {
  const restrictions = [].concat(location?.petRestrictions ?? []).filter(Boolean);
  if (restrictions.length) return restrictions;
  if (location?.petsAllowed === true) return 'Allowed';
  if (location?.petsAllowed === false) return 'Not allowed';
  return null;
};

const Field = ({ field, location }) => {
  const val = field.value ? field.value(location) : location?.[field.uuid];
  const [policyOpen, setPolicyOpen] = React.useState();

  if (field.uuid === 'petRestrictions' && (hasValue(val) || location?.animalPolicy)) {
    return (
      <FieldWrapper>
        <Icon size={18} name={field.icon} />
        <div className="fieldBody">
          <div className="fieldLabel">{field.label}</div>
          <div className="fieldContent">
            {Array.isArray(val) ? val?.join(', ') : val}{' '}
            <>
              {location?.animalPolicy && (
                <>
                  {hasValue(val) && <br />}
                  <Button
                    style={{ fontSize: hasValue(val) ? 12 : undefined }}
                    variant="link"
                    onClick={() => setPolicyOpen(true)}>
                    Pet Policy
                  </Button>
                </>
              )}
            </>
          </div>
        </div>
        {policyOpen && (
          <PetPolicyModal content={location?.animalPolicy} onClose={() => setPolicyOpen(false)} />
        )}
      </FieldWrapper>
    );
  }

  if (!hasValue(val)) return <></>;

  return (
    <FieldWrapper>
      <Icon size={18} name={field.icon} />
      <div className="fieldBody">
        <div className="fieldLabel">{field.label}</div>
        <div className="fieldContent">{Array.isArray(val) ? val?.join(', ') : val}</div>
      </div>
    </FieldWrapper>
  );
};

const fields = [
  {
    label: 'Available Date',
    icon: 'fa-solid fa-calendar-star',
    uuid: 'availableDate',
    value: (location) => {
      const pDate = new Date(location?.displayInfo?.availableDate);
      if (isNaN(pDate)) return '';

      if (pDate.getTime() < new Date().getTime()) return 'Now';

      return pDate.toLocaleDateString();
    }
  },
  {
    label: 'Lease Terms',
    icon: 'fa-solid fa-ruler',
    uuid: 'leaseTerm',
    value: (location) => location?.displayInfo?.leaseTerms
  },
  {
    label: 'Address',
    icon: 'fa-solid fa-location-dot',
    uuid: 'address.formattedAddress',
    value: (location) => location?.address?.formattedAddress
  },
  { label: 'Type', icon: 'fa-solid fa-building-memo', uuid: 'buildingType' },
  { label: 'Pets', icon: 'fa-solid fa-paw', uuid: 'petRestrictions', value: getPetsValue },
  { label: 'Parking', icon: 'fa-solid fa-garage-open', uuid: 'parkingType' },
  { label: 'Laundry', icon: 'fa-duotone fa-solid fa-washing-machine', uuid: 'laundryType' },
  { label: 'Heating', icon: 'fa-solid fa-temperature-three-quarters', uuid: 'heatingType' },
  { label: 'Cooling', icon: 'fa-solid fa-snowflake', uuid: 'coolingType' }
];

export default ({ location }) => {
  return (
    <Wrapper>
      <FieldsGrid>
        {fields.map((f, i) => (
          <Field key={i} field={f} location={location} />
        ))}
      </FieldsGrid>
      {location?.displayInfo?.amenities?.length > 0 && (
        <Row>
          <Details className="details">
            <div className="detailsLabel">Other amenities</div>
            <Amenities>
              {location?.displayInfo?.amenities?.map((am) => (
                <div key={am}>{am}</div>
              ))}
            </Amenities>
          </Details>
        </Row>
      )}
      {!!location?.marketingDescription && (
        <Row>
          <Details className="details">
            <div className="detailsLabel">Details</div>
            <div>
              <div
                className="descriptionContainer"
                dangerouslySetInnerHTML={{ __html: location?.marketingDescription }}
              />
            </div>
          </Details>
        </Row>
      )}
    </Wrapper>
  );
};
